/**
 * ============================================================================
 * HomeConnectModalFormNew — Service (API calls)
 * ============================================================================
 * Pure fetch wrappers matching pre-MVC utils.ts endpoints/payloads.
 * Home-specific: lead-check PhoneNo = `${countryCode}-${phoneNumber}`.
 * Always posts to lead-create-new (no LP branch).
 * ============================================================================
 */

import { formatDate, generateUniqueId } from "@/components/molecules/Forms/ConnectModalForm/utils";
import { leadCreateFetch } from "@/utils/connectCounsellor";

export interface LeadCheckPayload {
  phoneNumber: string;
  emailId: string;
  fullName: string;
  countryCode: string;
}

/** Ensures/returns the anonymous uniqueLeadId used for lead-check dedupe. */
export function getOrCreateUniqueLeadId(): string {
  let uniqueId = localStorage.getItem("uniqueLeadId");
  if (!uniqueId) {
    uniqueId = generateUniqueId();
    localStorage.setItem("uniqueLeadId", uniqueId);
  }
  return uniqueId;
}

/**
 * POST /api/leadsquared.com/lead-check — silent, best-effort dedupe ping.
 * PhoneNo = `${countryCode}-${phoneNumber}` (Home-specific; not phone-only).
 */
export async function checkExistingLead(payload: LeadCheckPayload): Promise<void> {
  try {
    const response = await fetch("/api/leadsquared.com/lead-check", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        PhoneNo: `${payload.countryCode}-${payload.phoneNumber}`,
        EmailID: payload.emailId,
        Name: payload.fullName,
        CountryCode: payload.countryCode,
        PageUrl: window.location.href,
        CommConsent: formatDate(new Date()),
        UniqueId: getOrCreateUniqueLeadId(),
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to submit phone number");
    }
  } catch (error) {
    console.error("Error submitting phone number:", error);
  }
}

export interface CreateLeadOptions {
  form: Record<string, any>;
  tracking: Record<string, any>;
  /** Passed through as `ipstack` — Home lead call uses getIpJSONData(). */
  ipstack: any;
  formExtra?: Record<string, any>;
  endpoint: string;
}

/**
 * POST /api/salesforce.com/lead-create-new. OR /api/salesforce.com/lead-create-lp-new
 * Returns the raw Response (caller parses JSON), matching pre-MVC leadSquareApiCall.
 */
export async function createLead({
  form,
  tracking,
  ipstack,
  formExtra,
  endpoint
}: CreateLeadOptions): Promise<Response> {
  return leadCreateFetch(`/api/salesforce.com/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ipstack,
      form: { ...form, ...formExtra },
      meta: {
        ...tracking,
        utm_medium: tracking?.utm_medium || "request_a_callback",
      },
    }),
  });
}

/** POST /api/connect-counsellor. Returns raw Response (caller parses). */
export { connectCounsellor } from "@/utils/connectCounsellor";
